import { Demographic, EventStructure } from "@/inference/engine/persist/types";
import { ViewTimeInference } from "../dataprocessor/view-time";
import { compileEvents } from "../inference/engine/persist/compile";
import { InferenceSetup } from "../inference/setup";
import { ActiveMemory, CategoryMemory } from "./types";
import { DataStore } from "@/datastore";
import { getMaxValueOfTag } from "./utils";

export class Memory {
  private static PERSIST_INTERVAL = 4500;
  private static SCORE_THRESHOLD = 0.7;
  private static LANDING_TAGS_KEY = "landing_page_tags";

  static init() {
    InferenceSetup.registerInterval(
      setInterval(() => Memory.persist(), Memory.PERSIST_INTERVAL),
    );
  }

  static recordLandingPageTags(categories: string[]) {
    if (Memory.getLandingTags()) return;

    if (categories.length) {
      sessionStorage.setItem(
        Memory.LANDING_TAGS_KEY,
        JSON.stringify([...new Set(categories)]),
      );
    }
  }

  static getLandingTags() {
    const tags = sessionStorage.getItem(Memory.LANDING_TAGS_KEY);
    return tags ? JSON.parse(tags) : null;
  }

  static persist(compiledMemory?: EventStructure) {
    try {
      const activeMemory = Memory.compileActiveMemory(compiledMemory);
      // const passiveMemory = DataStore.memoryStore.getStoredMemories();

      DataStore.memoryStore.set(activeMemory);
    } catch (e) {
      console.log(e, " ACTIVE MEMORY ERROR ");
    }
  }

  private static compileActiveMemory(
    compiledMemory?: EventStructure,
  ): ActiveMemory {
    const { demographic } = compiledMemory ?? compileEvents();
    const tags = DataStore.getCleanNestedTags();

    const subcategory = tags?.subcategory;

    const inferredTags = Object.keys(subcategory ?? {});
    const significantCategories = inferredTags.filter(
      (tag) => (subcategory[tag] || 0) > Memory.SCORE_THRESHOLD,
    );

    const categoryMemories: Record<string, CategoryMemory> = {};

    significantCategories.forEach((category, i, arr) => {
      if (!category) return;

      const categorySpecificTags =
        DataStore.getCategorySpecificInference()[category.toLowerCase()];

      const props = {
        categoryIntent: categorySpecificTags?.subcategory?.[category] || 0,
        demographic,
        categoryTags: categorySpecificTags,
      };

      categoryMemories[category] = {
        demographics: demographic || {},
        behavioral: {
          funnelStage: sessionStorage.getItem("funnelStage") || "viewer",
          understanding: Memory.inferUnderstandingLevel(props),
          purchasingFor: Memory.inferPurchasingFor(props),

          languageStrength: Memory.inferLanguageStrength(props),
          seekingAction: Memory.inferSeekingAction(props),
          preferences: Memory.inferPreferences(props),
        },
        intent: {
          landingCategory: Memory.inferLandingCategory(),
          browsingPreferences: arr,
        },
      };
    });

    // console.clear();
    // console.log(categoryMemories);

    return categoryMemories;
  }

  private static inferUnderstandingLevel(props: {
    categoryIntent: number;
    demographic: Demographic;
    categoryTags: any;
  }) {
    const { categoryIntent } = props;
    const intentMultiple = categoryIntent / Memory.SCORE_THRESHOLD;

    if (intentMultiple > 3) {
      return "high";
    } else if (intentMultiple > 2) {
      return "medium";
    }
    return "low";
  }

  private static inferPurchasingFor(props: {
    categoryIntent: number;
    demographic: Demographic;
    categoryTags: any;
  }): CategoryMemory["behavioral"]["purchasingFor"] {
    const { categoryTags } = props;
    const usageTags = categoryTags?.tags?.usage;

    const findDominantValue = (obj: Record<string, number>) => {
      if (!obj) return null;

      const entries = Object.entries(obj ?? {});
      const sorted = entries.sort((a, b) => b[1] - a[1]);

      if (sorted.length < 2) {
        return null;
      }

      const [highest, secondHighest] = sorted;

      if (highest[1] > secondHighest[1] * 2) {
        return highest[0].toLowerCase();
      }

      return null;
    };

    if (!usageTags) {
      return {
        type: "person",
        value: "",
      };
    }

    const dominantOccasion = findDominantValue(usageTags.occasions);
    const dominantSeason = findDominantValue(usageTags.season);
    const dominantActivity = findDominantValue(usageTags.activities);

    if (dominantOccasion) {
      return { type: "ocassion", value: dominantOccasion };
    }

    if (dominantSeason) {
      return { type: "season", value: dominantSeason };
    }

    if (dominantActivity) {
      return { type: "activity", value: dominantActivity };
    }

    return {
      type: "person",
      value: findDominantValue(usageTags?.ageGroup) || "",
    };
  }

  private static inferLanguageStrength(props: {
    categoryIntent: number;
    demographic: Demographic;
    categoryTags: any;
  }) {
    const { demographic, categoryIntent } = props;

    const languageExpertiseIndicators: Record<
      keyof Demographic,
      Record<string, number>
    > = {
      education_level: {
        graduate: 3,
        postgraduate: 3,
        undergraduate: 2,
        highschool: 1,
      },
      occupation: {
        professional: 3,
        skilled: 2,
        student: 1,
      },
      age: {},
      gender: {},
      income_level: {},
      marital_status: {},
      family_structure: {},
    };

    let totalScore = 0;
    let maxPossibleScore = 0;

    if (demographic.education_level) {
      const educationLevel =
        demographic.education_level as keyof typeof languageExpertiseIndicators.education_level;
      totalScore +=
        languageExpertiseIndicators.education_level[educationLevel] || 0;
      maxPossibleScore += 3;
    }

    if (demographic.occupation) {
      const professionType =
        demographic.occupation as keyof typeof languageExpertiseIndicators.occupation;
      totalScore += languageExpertiseIndicators.occupation[professionType] || 0;
      maxPossibleScore += 3;
    }

    const normalizedScore =
      maxPossibleScore > 0 ? totalScore / maxPossibleScore : 0;

    if (categoryIntent > 80 || normalizedScore > 0.8) {
      return "expert";
    } else if (categoryIntent > 50 || normalizedScore > 0.4) {
      return "intermediate";
    }
    return "beginner";
  }

  private static inferSeekingAction(props: {
    categoryIntent: number;
    demographic: Demographic;
    categoryTags: any;
  }): CategoryMemory["behavioral"]["seekingAction"] {
    const { categoryIntent } = props;
    const logs = window.helium.logs || [];

    const uniquePageCount = new Set(logs.map((log) => log.pathname)).size;

    const subcategoryScores = ViewTimeInference.categorySpecificScore;

    const categoryScores = Object.values(subcategoryScores).map((score) =>
      Object.values(score || {}).reduce(
        (sum: number, val: unknown) =>
          sum + (typeof val === "number" ? val : 0),
        0,
      ),
    );

    const maxSubcategoryScore = Math.max(...categoryScores);

    const totalScore = categoryScores.reduce((sum, score) => sum + score, 0);

    if (maxSubcategoryScore > 300 && uniquePageCount > 5) {
      // high page view and high intent on one category
      return "recommendation";
    }

    if (totalScore > 300 && maxSubcategoryScore < 50) {
      //  spread out intent
      return "guidance";
    }

    if (uniquePageCount <= 2 && categoryIntent > 70) {
      // low page view and high intent on one category
      return "decision";
    }

    return "guidance";
  }

  private static inferPreferences(props: {
    categoryIntent: number;
    demographic: Demographic;
    categoryTags: any;
  }): CategoryMemory["behavioral"]["preferences"] {
    // const functionalTags = props?.categoryTags?.tags?.functional;
    // const visualTags = props?.categoryTags?.tags?.visual;
    // const physicalTags = props?.categoryTags?.physical;
    // const maxValuesInFunctionalTags = getMaxValueOfTag(functionalTags);
    // const maxValuesInVisualTags = getMaxValueOfTag(visualTags);
    // const maxValuesInPhyiscalTags = getMaxValueOfTag(physicalTags);

    const otherTags = Object.fromEntries(
      Object.entries(props?.categoryTags?.tags ?? {}).map(([tag, value]) => {
        console.log({ tag, value });
        if (!tag) return [];
        const maxValues = getMaxValueOfTag(value);

        return [tag, maxValues];
      }),
    );

    function getAllNonZeroValues(prop: Record<string, number>) {
      return Object.keys(prop ?? {}).filter((key) => prop[key] > 0);
    }

    const preferences: string[] = getAllNonZeroValues(
      props.categoryTags?.tags?.market?.sustainability,
    );

    if (props.categoryTags?.priceElasticity > 70) {
      preferences.push("price_sensitive");
    }
    if (
      window.helium.attributes?.ecommerce?.secondary?.discountSensitivity > 70
    ) {
      preferences.push("discount_seeker");
    }
    return {
      // functional: maxValuesInFunctionalTags,
      values: preferences,
      // visual: maxValuesInVisualTags,
      // physical: maxValuesInPhyiscalTags,
      ...otherTags,
    };
  }

  private static inferLandingCategory(): string {
    const landingTags = Memory.getLandingTags() as string[];

    if (!landingTags) {
      return "";
    }

    const frequencyMap = landingTags.reduce(
      (acc, tag) => {
        if (!tag) return acc;
        const lowercaseTag = tag?.toLowerCase();
        acc[lowercaseTag] = (acc[lowercaseTag] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );

    const entries = Object.entries(frequencyMap ?? {});

    if (!entries.length) {
      return "";
    }

    const mostFrequent = entries.reduce((max, current) =>
      current[1] > max[1] ? current : max,
    );

    return mostFrequent[0] || landingTags[0] || "";
  }
}
